/**
 * Shopify Cart Drawer Controller
 * Handles slide-out animations, cart AJAX API interactions (/cart.js, /cart/change.js),
 * money formatting, dynamic DOM updates, free-shipping progress bar, and suggested product upsell.
 */
class ShopifyCartDrawer {
  constructor() {
    this.container = document.getElementById('ShopifyCartDrawer');
    if (!this.container) return;
 
    // Config injected by Liquid
    const cfg = window.__cartDrawerConfig || {};
    this.FREE_SHIPPING_THRESHOLD = cfg.freeShippingThreshold || 5000; // cents
 
    // Suggested product config (may be null if no product set in theme settings)
    // this.suggestedProduct = cfg.suggestedProductId
    //   ? {
    //       id: cfg.suggestedProductId,
    //       variantId: cfg.suggestedProductVariantId,
    //       title: cfg.suggestedProductTitle || '',
    //       price: cfg.suggestedProductPrice || 0,
    //       image: cfg.suggestedProductImage || '',
    //     }
    //   : null;
    this.suggestedProduct = null;
 
    // ── CHECKOUT GATING STATE ────────────────────────────────────────────────
    // Checkout is enabled by default. It only becomes disabled at the exact
    // moment the suggestion's "+ Add" button (data-drawer-suggestion-add) is
    // clicked, and re-enables the moment that add request finishes
    // successfully. It is never locked just because a suggestion exists.
    this.checkoutLocked = false;
    this.checkoutBtn = document.querySelector('.cart-drawer-checkout-btn');
    this.cartForm = this.checkoutBtn ? this.checkoutBtn.closest('form') : null;
 
    // DOM refs
    this.overlay        = this.container.querySelector('.cart-drawer-overlay');
    this.panel          = this.container.querySelector('.cart-drawer-panel');
    this.itemsContainer = this.container.querySelector('[data-drawer-items-container]');
    this.countBadge     = this.container.querySelector('[data-drawer-cart-count]');
    this.footer         = this.container.querySelector('[data-drawer-footer]');
    this.subtotalEl     = this.container.querySelector('[data-drawer-cart-subtotal]');
    this.shippingBar    = this.container.querySelector('[data-drawer-shipping-bar]');
    this.shippingText   = this.container.querySelector('[data-drawer-shipping-text]');
    this.shippingFill   = this.container.querySelector('[data-drawer-shipping-fill]');
    this.shippingPct    = this.container.querySelector('[data-drawer-shipping-pct]');
 
    // Currency
    this.currencySymbol = cfg.currencySymbol || '$';
    this.detectCurrency();
 
    // Bind
    this.open          = this.open.bind(this);
    this.close         = this.close.bind(this);
    this.refresh       = this.refresh.bind(this);
    this.removeItem    = this.removeItem.bind(this);
    this.updateQuantity = this.updateQuantity.bind(this);
 
    this.currentLocationConfig = undefined;
    this.lastCartTotal = (window.__cartDrawerConfig || {}).cartTotal || 0;
    // Cache of variantId → compare_at_price (in cents), seeded from Liquid on page load.
    // The cart API (/cart.js) can return null for compare_at_price when Shopify Scripts
    // or price rules are active, so Liquid-sourced values are the reliable fallback.
    this.compareAtCache = Object.assign({}, cfg.compareAtPrices || {});
    this.init();
  }
 
  detectCurrency() {
    const cfg = window.__cartDrawerConfig || {};
    // Use shop.money_format from Liquid — most reliable source
    if (cfg.moneyFormat) {
      this.moneyFormat = cfg.moneyFormat;
    }
    // Fallback: read from DOM
    if (this.subtotalEl) {
      const text = this.subtotalEl.textContent.trim();
      const match = text.match(/^([^\d\s,.]+)/);
      if (match && match[0]) this.currencySymbol = match[0];
    }
  }
 
  init() {
    // Close buttons
    this.container.querySelectorAll('[data-drawer-close]').forEach(btn =>
      btn.addEventListener('click', this.close)
    );
 
    // Overlay backdrop
    if (this.overlay) this.overlay.addEventListener('click', this.close);
 
    // Escape key
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
 
    // Intercept /cart links and [data-cart-drawer-trigger] buttons
    document.addEventListener('click', (e) => {
      const target = e.target.closest('a[href="/cart"], [data-cart-drawer-trigger]');
      if (target) { e.preventDefault(); this.open(); }
    });
 
    // Delegated: remove items
    if (this.itemsContainer) {
      this.itemsContainer.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-drawer-remove-item]');
        if (removeBtn) {
          const key = removeBtn.getAttribute('data-drawer-remove-item');
          if (key) this.removeItem(key);
        }
      });
 
      // Delegated: suggested product "+ Add" button
      this.itemsContainer.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-drawer-suggestion-add]');
        if (addBtn) {
          const card = addBtn.closest('[data-drawer-suggestion]');
          const variantId = card ? card.getAttribute('data-suggestion-variant') : null;
          if (variantId) this.addSuggestion(variantId, addBtn);
        }
      });
    }
 
    // Custom events
    document.addEventListener('cart:open', this.open);
    document.addEventListener('cart:close', this.close);
    document.addEventListener('cart:refresh', this.refresh);
 
    // AJAX interceptors
    this.setupAjaxInterceptors();
    this.initLocationUI();
    this.initCheckoutGate();
 
    window.addEventListener('beforeunload', () => {
      document.body.style.overflow = '';
    });
  }
 
  setupAjaxInterceptors() {
    const self = this;
 
    if (window.fetch) {
      const originalFetch = window.fetch;
      window.fetch = function(...args) {
        return originalFetch.apply(this, args).then((response) => {
          if (response && response.url && response.url.includes('/cart/add')) {
            setTimeout(() => self.open(), 150);
          }
          return response;
        });
      };
    }
 
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      this._url = url;
      return originalOpen.apply(this, arguments);
    };
 
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function() {
      this.addEventListener('load', () => {
        if (this._url && this._url.includes('/cart/add')) {
          setTimeout(() => self.open(), 150);
        }
      });
      return originalSend.apply(this, arguments);
    };
  }
 
  isOpen() {
    return this.container.classList.contains('is-open');
  }
 
 
  async fetchSuggestion(cartItems) {
    if (!cartItems.length) return null;
 
    const firstProductId = cartItems[0].product_id;
    const cartProductIds = cartItems.map(i => i.product_id);
 
    try {
      const res = await fetch(
        `/recommendations/products.json?product_id=${firstProductId}&limit=6&intent=related`
      );
      if (!res.ok) return null;
      const data = await res.json();
 
      const pick = data.products.find(p =>
        p.available && !cartProductIds.includes(p.id)
      );
      if (!pick) return null;
 
      return {
        id: pick.id,
        variantId: pick.variants[0].id,
        title: pick.title,
        price: pick.price,
        image: pick.images[0] ? pick.images[0] : ''
      };
    } catch (e) {
      return null;
    }
  }
 
  open() {
    if (this.isOpen()) return;
    this.container.classList.add('is-open');
    this.container.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.refresh();
  }
 
  close() {
    if (!this.isOpen()) return;
    this.container.classList.remove('is-open');
    this.container.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
 
  formatMoney(cents) {
    // Use Shopify's native formatter if available — handles all locales
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }
    // Fallback using shop.money_format pattern
    if (this.moneyFormat) {
      const amount = (cents / 100).toFixed(2);
      return this.moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
    }
    // Last resort
    return `${this.currencySymbol}${(cents / 100).toFixed(2)}`;
  }
 
  escapeHtml(string) {
    if (!string) return '';
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return string.replace(/[&<>"']/g, (m) => map[m]);
  }
 
  // ── FREE SHIPPING BAR ───────────────────────────────────────────────────────
 
  /**
   * Update the shipping progress bar to reflect the current cart total.
   * @param {number} totalCents – cart.total_price in cents
   */
  updateShippingBar(totalCents) {
    this.lastCartTotal = totalCents;
    if (this.currentLocationConfig !== undefined) {
      this.updateShippingBarWithConfig(totalCents, this.currentLocationConfig || null);
    }
  }
 
  // ── SUGGESTED PRODUCT ────────────────────────────────────────────────────────
 
  /**
   * Returns true if the suggested product is already in the given cart items array.
   */
  isSuggestionInCart(cartItems) {
    if (!this.suggestedProduct) return true; // nothing to suggest
    return cartItems.some(item => item.product_id === this.suggestedProduct.id);
  }
 
  /**
   * Renders the suggested product card HTML.
   */
  renderSuggestion() {
    const p = this.suggestedProduct;
    if (!p) return '';
 
    const imageHtml = p.image
      ? `<img src="${p.image}" alt="${this.escapeHtml(p.title)}" loading="lazy">`
      : '';
 
    return `
      <div class="cart-drawer-suggestion" data-drawer-suggestion data-suggestion-variant="${p.variantId}">
        <div class="cart-drawer-suggestion__img-wrap">${imageHtml}</div>
        <div class="cart-drawer-suggestion__details">
          <span class="cart-drawer-suggestion__kicker">Pairs with your order</span>
          <span class="cart-drawer-suggestion__name">${this.escapeHtml(p.title)}</span>
          <span class="cart-drawer-suggestion__price">${this.formatMoney(p.price)}</span>
        </div>
        <button type="button" class="cart-drawer-suggestion__add" data-drawer-suggestion-add>
          + Add
        </button>
      </div>
    `;
  }
 
  /**
   * Adds the suggested product to the cart, then refreshes the drawer.
   * Drives the button-level loading state AND the checkout gate —
   * scoped entirely to data-drawer-suggestion-add, no other button is touched.
   *
   * Checkout lock timeline:
   *   click "+ Add"  -> checkout disabled immediately
   *   request resolves ok -> checkout re-enabled (+ brief success flash)
   *   request fails -> checkout re-enabled (no flash); button resets to "+ Add"
   */
  async addSuggestion(variantId, btn) {
    if (!variantId) return;
 
    const originalLabel = btn.dataset.originalLabel || btn.textContent.trim();
    btn.dataset.originalLabel = originalLabel;
 
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.innerHTML = '<span class="cart-drawer-suggestion__spinner" aria-hidden="true"></span><span class="sr-only">Adding…</span>';
 
    // Lock checkout the instant this specific "+ Add" is clicked.
    this.lockCheckout();
 
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!response.ok) throw new Error('Failed to add suggestion');
 
      await this.refresh();
 
      // Unlock only after the add has been confirmed AND the drawer has
      // re-rendered with the updated cart.
      this.unlockCheckout({ animate: true });
    } catch (error) {
      console.error('ShopifyCartDrawer suggestion error:', error);
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.textContent = originalLabel;
      // Add failed — release the lock so the user isn't stuck, but no
      // success animation since nothing was actually added.
      this.unlockCheckout({ animate: false });
    }
  }
 
 
  getDeliveryText() {
    const today = new Date();
    const d1 = new Date(today);
    const d2 = new Date(today);
    d1.setDate(today.getDate() + 2);
    d2.setDate(today.getDate() + 3);
 
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const fmt = (d) => `${d.getDate()} ${months[d.getMonth()]}`;
 
    return `Estimated Delivery · ${fmt(d1)} — ${fmt(d2)}`;
  }
 
  // ── CHECKOUT GATE ────────────────────────────────────────────────────────
  // Purely event-driven, scoped to .cart-drawer-checkout-btn:
  //   - Enabled by default on page load.
  //   - Locked the instant data-drawer-suggestion-add is clicked (lockCheckout).
  //   - Unlocked the instant that add request resolves, success or failure
  //     (unlockCheckout). No standing rule re-locks it afterward — it is
  //     never disabled just because a suggestion exists in the drawer.
 
  initCheckoutGate() {
    if (!this.checkoutBtn) return;
 
    // Safety net: even if the button's `disabled` attribute were somehow
    // bypassed (e.g. keyboard form submit), block the actual form submit
    // while a suggestion add is in flight.
    if (this.cartForm) {
      this.cartForm.addEventListener('submit', (e) => {
        if (this.checkoutLocked) {
          e.preventDefault();
          e.stopPropagation();
          this.pulseCheckoutLocked();
        }
      }, true); // capture phase, so it runs before other submit handlers
    }
 
    // Belt-and-suspenders click guard (covers buttons not in a <form>, or
    // themes that handle checkout via JS instead of native form submit).
    this.checkoutBtn.addEventListener('click', (e) => {
      if (this.checkoutLocked) {
        e.preventDefault();
        e.stopPropagation();
        this.pulseCheckoutLocked();
      }
    }, true);
  }
 
  setCheckoutEnabled(enabled) {
    if (!this.checkoutBtn) return;
    this.checkoutBtn.disabled = !enabled;
    this.checkoutBtn.classList.toggle('cart-drawer-checkout-btn--locked', !enabled);
    this.checkoutBtn.setAttribute('aria-disabled', String(!enabled));
  }
 
  /**
   * Called the instant data-drawer-suggestion-add is clicked.
   */
  lockCheckout() {
    this.checkoutLocked = true;
    this.setCheckoutEnabled(false);
  }
 
  /**
   * Called when that add request resolves (success or failure).
   */
  unlockCheckout({ animate = false } = {}) {
    this.checkoutLocked = false;
    this.setCheckoutEnabled(true);
    if (!this.checkoutBtn) return;
    if (animate) {
      this.checkoutBtn.classList.add('cart-drawer-checkout-btn--unlocked');
      window.setTimeout(() => {
        this.checkoutBtn.classList.remove('cart-drawer-checkout-btn--unlocked');
      }, 900);
    }
  }
 
  /**
   * Small shake/pulse to communicate "still locked" when a blocked click
   * or submit happens while an add is in flight.
   */
  pulseCheckoutLocked() {
    if (!this.checkoutBtn) return;
    this.checkoutBtn.classList.remove('cart-drawer-checkout-btn--shake');
    // Force reflow so the animation can re-trigger on repeated clicks.
    void this.checkoutBtn.offsetWidth;
    this.checkoutBtn.classList.add('cart-drawer-checkout-btn--shake');
  }
 
  // ── LOCATION / POSTCODE ──────────────────────────────────
 
  LOCATION_KEY = 'jm_cart_location';
 
  LOCATION_CONFIGS = {
    au: {
      threshold: 5000,
      thresholdLabel: '$50',
      deliveryMin: 2,
      deliveryMax: 8,
      label: 'Australia',
      hasFreeShipping: true
    },
    nz: {
      threshold: 12000,
      thresholdLabel: '$120',
      deliveryMin: 5,
      deliveryMax: 12,
      label: 'New Zealand',
      hasFreeShipping: true
    },
    other: {
      threshold: null,
      thresholdLabel: null,
      deliveryMin: 6,
      deliveryMax: 27,
      label: 'International',
      hasFreeShipping: false
    }
  };
  async detectLocationByPostcode(postcode, country) {
    const pc = postcode.trim().replace(/\s/g, '');
 
    // If user selected Other, skip API
    if (country === 'other') {
      return { region: 'other', postcode: pc, ...this.LOCATION_CONFIGS.other };
    }
 
    try {
      const res = await fetch(`https://api.zippopotam.us/${country}/${pc}`);
      if (res.ok) {
        return { region: country, postcode: pc, ...this.LOCATION_CONFIGS[country] };
      } else {
        return null; // postcode not valid for selected country
      }
    } catch (e) {
      return null;
    }
  }
 
  saveLocation(postcode, config) {
    try {
      localStorage.setItem(this.LOCATION_KEY, JSON.stringify({ postcode, config }));
    } catch(e) {}
  }
 
  loadLocation() {
    try {
      const saved = localStorage.getItem(this.LOCATION_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch(e) { return null; }
  }
 
  clearLocation() {
    try { localStorage.removeItem(this.LOCATION_KEY); } catch(e) {}
  }
 
  getDeliveryRange(config) {
    const today = new Date();
    const d1 = new Date(today);
    const d2 = new Date(today);
    // Add 2 days processing + delivery range
    d1.setDate(today.getDate() + 2 + config.deliveryMin);
    d2.setDate(today.getDate() + 2 + config.deliveryMax);
 
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const fmt = (d) => `${d.getDate()} ${months[d.getMonth()]}`;
 
    return `Estimated Delivery · ${fmt(d1)} — ${fmt(d2)}`;
  }
 
  initLocationUI() {
    const locationEl  = document.getElementById('cart-drawer-location');
    const progressEl  = document.getElementById('cart-drawer-shipping-progress');
    const input       = document.getElementById('cart-drawer-postcode');
    const submitBtn   = document.getElementById('cart-drawer-postcode-submit');
    const errorEl     = document.getElementById('cart-drawer-postcode-error');
    const changeBtn   = document.getElementById('cart-drawer-change-location');
 
    if (!locationEl || !progressEl) return;
 
    const showProgress = (config) => {
      locationEl.style.display  = 'none';
      progressEl.style.display  = 'block';
      this.currentLocationConfig = config;
      this.updateShippingBarWithConfig(this.lastCartTotal || 0, config);
      const deliveryEl = this.footer ? this.footer.querySelector('.cart-drawer-delivery') : null;
      if (deliveryEl) deliveryEl.textContent = this.getDeliveryRange(config);
    };
 
    const showInput = () => {
      locationEl.style.display  = 'block';
      progressEl.style.display  = 'none';
      this.currentLocationConfig = null;
      if (input) input.value = '';
      if (errorEl) errorEl.textContent = '';
    };
 
    const handleSubmit = async () => {
      const val        = input ? input.value.trim() : '';
      const countryEl  = document.getElementById('cart-drawer-country');
      const countryVal = countryEl ? countryEl.value : '';
 
      if (!countryVal) {
        if (errorEl) errorEl.textContent = 'Please select a country.';
        return;
      }
      if (!val && countryVal !== 'other') {
        if (errorEl) errorEl.textContent = 'Please enter a postcode.';
        return;
      }
      if (errorEl) errorEl.textContent = '';
 
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = '...'; }
 
      try {
        const config = await this.detectLocationByPostcode(val, countryVal);
        if (!config) {
          if (errorEl) errorEl.textContent = 'Postcode not found for selected country.';
          return;
        }
        this.saveLocation(val, config);
        showProgress(config);
      } catch (e) {
        if (errorEl) errorEl.textContent = 'Could not detect location. Try again.';
      } finally {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Go'; }
      }
    };
 
    if (submitBtn) submitBtn.addEventListener('click', () => handleSubmit());
    if (input) {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') handleSubmit();
      });
    }
 
    if (changeBtn) {
      changeBtn.addEventListener('click', () => {
        this.clearLocation();
        showInput();
      });
    }
 
    // ── NEW DETECTION FLOW ──────────────────────────────────
    // 1. Check localStorage first (manual override takes priority)
    const saved = this.loadLocation();
    if (saved && saved.config) {
      showProgress(saved.config);
      return;
    }
 
    // 2. Use Shopify's IP-based country (passed from Liquid)
    const shopifyCountry = (window.__cartDrawerConfig || {}).shopifyCountry || '';
    const autoConfig = this.LOCATION_CONFIGS[shopifyCountry] || null;
 
    if (autoConfig) {
      // Known country (au or nz) — show bar directly, no postcode needed
      // Don't save to localStorage so "Change location" truly resets to auto-detect
      showProgress(autoConfig);
    } else {
      // Unknown/international — show bar with "calculated at checkout" message
      showProgress(this.LOCATION_CONFIGS['other']);
    }
    // ── END NEW DETECTION FLOW ──────────────────────────────
  }
 
  updateShippingBarWithConfig(totalCents, config) {
    if (!this.shippingBar) return;
 
    const fillEl = this.shippingBar.querySelector('[data-drawer-shipping-fill]');
    const textEl = this.shippingBar.querySelector('[data-drawer-shipping-text]');
 
    if (!config || !config.hasFreeShipping) {
      if (textEl) textEl.innerHTML = `<span>Shipping rates calculated at checkout</span>`;
      if (fillEl) { fillEl.style.width = '0%'; fillEl.classList.remove('cart-drawer-shipping-bar__fill--unlocked'); }
      return;
    }
 
    const threshold = config.threshold;
    const remaining = threshold - totalCents;
    const pct = Math.min(100, Math.round((totalCents / threshold) * 100));
    const unlocked = remaining <= 0;
 
    if (fillEl) {
      fillEl.style.width = `${pct}%`;
      fillEl.classList.toggle('cart-drawer-shipping-bar__fill--unlocked', unlocked);
    }
 
    if (textEl) {
      if (unlocked) {
        textEl.innerHTML = `<span>✓ Free shipping unlocked</span>`;
      } else {
        textEl.innerHTML = `
          <span>${this.formatMoney(remaining)} away from free shipping</span>
          <span class="cart-drawer-shipping-bar__pct">${pct}%</span>
        `;
      }
    }
  }
  // ── ITEM RENDER ─────────────────────────────────────────────────────────────
 
  renderItem(item) {
    const imageHtml = item.image
      ? `<img src="${item.image}" alt="${this.escapeHtml(item.product_title)}" loading="lazy">`
      : `<div style="width:100%;height:100%;background:var(--jm-paper-warm);"></div>`;
 
    const sku = item.sku ? item.sku : `SKU-${item.variant_id}`;
 
    const variantHtml = (item.variant_title && item.variant_title !== 'Default Title')
      ? `<span class="cart-drawer-item-meta">${this.escapeHtml(item.variant_title)}</span>`
      : '';
 
    const sellingPlanHtml = item.selling_plan_allocation
      ? `<span class="cart-drawer-item-plan">${this.escapeHtml(item.selling_plan_allocation.selling_plan.name)}</span>`
      : '';
 
    const qtyBadgeHtml = item.quantity > 1
      ? `<span class="cart-drawer-item-quantity-badge">QTY: ${item.quantity}</span>`
      : '';
 
    return `
      <div class="cart-drawer-item" data-cart-item-key="${item.key}">
        <div class="cart-drawer-item-img-wrapper">${imageHtml}</div>
        <div class="cart-drawer-item-details">
          <span class="cart-drawer-item-sku">${this.escapeHtml(sku)}</span>
          <span class="cart-drawer-item-name">${this.escapeHtml(item.product_title)}</span>
            ${variantHtml}
            ${sellingPlanHtml}
          <div class="cart-drawer-item-price-qty">
           

            <span class="cart-drawer-item-price">

${
this.compareAtCache[item.variant_id] > item.final_price
?
`<span class="old-price">
${this.formatMoney(this.compareAtCache[item.variant_id])}
</span>`
:
''
}

${this.formatMoney(item.final_price)}

</span>
            ${qtyBadgeHtml}
          </div>
        </div>
        <button type="button" class="cart-drawer-item-remove" aria-label="Remove ${this.escapeHtml(item.product_title)}" data-drawer-remove-item="${item.key}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `;
  }
 
  // ── MAIN REFRESH / DOM UPDATE ────────────────────────────────────────────────
 
  async refresh() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      const cartData = await response.json();

      cartData.items.forEach(item => {

  const variantId = String(item.variant_id);

  if (this.compareAtCache[variantId]) return;


  fetch(`/products/${item.handle || item.product_id}.js`)
  .then(res => res.json())
  .then(product => {


    const variant = product.variants.find(v => 
      String(v.id) === variantId
    );


    if (variant) {

      this.compareAtCache[variantId] = 
        variant.compare_at_price || variant.price;


      this.updateDOM(cartData);

    }


  })
  .catch(err => {
    console.log('compare price error', err);
  });


});
 
      // Only re-fetch suggestion if item count changed
      const newItemHash = cartData.items.map(i => i.product_id).join(',');
      if (cartData.items.length > 0 && newItemHash !== this._lastItemHash) {
        this._lastItemHash = newItemHash;
        this.suggestedProduct = await this.fetchSuggestion(cartData.items);
      } else if (cartData.items.length === 0) {
        this._lastItemHash = '';
        this.suggestedProduct = null;
      }
 
      this.updateDOM(cartData);
    } catch (error) {
      console.error('ShopifyCartDrawer error:', error);
    }
  }
 
  async updateQuantity(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('Failed to change quantity');
      const cartData = await response.json();
      this.updateDOM(cartData);
      document.dispatchEvent(new CustomEvent('cart:did-update', { detail: cartData }));
    } catch (error) {
      console.error('ShopifyCartDrawer error:', error);
    }
  }
 
  removeItem(key) {
    this.updateQuantity(key, 0);
  }
 
  updateDOM(cart) {
    // 1. Count badges
    if (this.countBadge) this.countBadge.textContent = cart.item_count;
    document.querySelectorAll('.cart-count-number').forEach(b => b.textContent = cart.item_count);
 
    // 2. Shipping bar
    this.updateShippingBar(cart.total_price);
 
    // 3. Items + optional suggestion card
    if (this.itemsContainer) {
      if (cart.items.length === 0) {
        this.itemsContainer.innerHTML = `<div class="cart-drawer-empty">Your cart is empty</div>`;
      } else {
        const itemsHtml = cart.items.map(item => this.renderItem(item)).join('');
        const suggestionInCart = this.isSuggestionInCart(cart.items);
        const suggestionHtml = !suggestionInCart
          ? this.renderSuggestion()
          : '';
        this.itemsContainer.innerHTML = itemsHtml + suggestionHtml;
 
        // If the suggested product is now present in the cart (added via
        // this card, or any other path), reflect that in the gate state.
        if (this.suggestedProduct && suggestionInCart) {
          this.suggestionAdded = true;
        }
      }
    }
 
    // 4. Footer
    if (this.footer) {
      if (cart.items.length === 0) {
        this.footer.style.display = 'none';
      } else {
        this.footer.style.display = 'flex';
        if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(cart.total_price);

        // Update compare-at (old) price dynamically.
        // We prefer the Liquid-seeded cache because the cart API can return
        // compare_at_price as null when Shopify Scripts/price rules are active.
const oldPriceEl = document.getElementById('drawer-old-price');

if (oldPriceEl) {

  let oldTotal = 0;
  let hasOldPrice = false;


  cart.items.forEach(item => {

    const variantId = String(item.variant_id);

    const oldPrice = Number(
      this.compareAtCache[variantId] || 0
    );



if (oldPrice > 0) {

  oldTotal += oldPrice * item.quantity;
  hasOldPrice = true;

}

  });


  if (hasOldPrice) {

    oldPriceEl.textContent = this.formatMoney(oldTotal);
    oldPriceEl.style.display = 'inline-block';

  } else {

    oldPriceEl.textContent = '';
    oldPriceEl.style.display = 'none';

  }

}
      }
    }
    const deliveryEl = this.footer ? this.footer.querySelector('.cart-drawer-delivery') : null;
    if (deliveryEl) {
      if (this.currentLocationConfig) {
        deliveryEl.textContent = this.getDeliveryRange(this.currentLocationConfig);
      } else {
        deliveryEl.textContent = '';
      }
    }
 
    // 5. Checkout gate — recompute every time cart/suggestion state changes
    this.syncCheckoutGateState();
  }
}
 
document.addEventListener('DOMContentLoaded', () => {
  window.ShopifyCartDrawer = new ShopifyCartDrawer();
});